import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Shield } from 'lucide-react';
import { useTenant, useTenants } from '@/store/tenant';
import { getIdpProvider } from '@/types';
import { useLayout } from '@/contexts/layout-context';
import { Loader } from '@/components/loader';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';

export const AddTenantIdpPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setBreadcrumbs } = useLayout();
  const tenantStore = useTenant(id);
  const getTenant = useTenants((state) => state.get);
  const [tenantName, setTenantName] = useState('');
  const [loading, setLoading] = useState(true);

  const back = `/m/tenants/${id}?tab=idps`;

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    Promise.all([getTenant(id), tenantStore.idps.list(), tenantStore.idps.listAvailable()])
      .then(([tenant]) => {
        if (cancelled) return;
        setTenantName(tenant.name);
        setBreadcrumbs([
          { name: 'Tenants', to: '/m/tenants' },
          { name: tenant.name, to: back },
          { name: 'Add Identity Provider' },
        ]);
      })
      .catch(console.error)
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  const configured = new Set(tenantStore.idps.data.map((c) => c.idp_id));
  const available = tenantStore.idps.available.filter((idp) => !configured.has(idp.id));

  return (
    <div className="p-4 lg:p-8 max-w-5xl mx-auto w-full animate-in fade-in duration-500">
      <PageHeader
        title="Add Identity Provider"
        subtitle={`Choose a provider to enable for ${tenantName}.`}
        actions={
          <Button variant="ghost" onClick={() => navigate(back)} className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
        }
      />

      <div className="mt-8 animate-in slide-in-from-bottom-4 duration-500">
        <Card title="Available providers" subtitle="Providers already configured for this tenant are not listed." className="glass">
          {available.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-500">No more identity providers are available to enable.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {available.map((idp) => {
                const meta = getIdpProvider(idp.provider);
                const logo = idp.logo_url || meta?.logoUrl;
                return (
                  <button
                    key={idp.id}
                    type="button"
                    onClick={() => navigate(`/m/tenants/${id}/idps/${idp.id}`)}
                    className="flex items-center gap-4 p-4 text-left rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-(--primary) hover:shadow-md transition-all group"
                  >
                    <span className="w-12 h-12 shrink-0 rounded-lg border border-slate-200/60 dark:border-slate-700 bg-white flex items-center justify-center overflow-hidden">
                      {logo ? <img src={logo} alt="" className="w-8 h-8 object-contain" /> : <Shield className="w-6 h-6 text-slate-400" />}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-bold text-slate-900 dark:text-white truncate">{idp.display_name}</span>
                      <span className="block text-[11px] font-medium uppercase tracking-wider text-slate-500">
                        {meta?.name ?? idp.provider}
                      </span>
                    </span>
                    <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-(--primary) transition-colors" />
                  </button>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};
